import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

// Groups every expense that occurred during the year by its underlying id,
// so a recurring expense's occurrences (including ones that repeat more
// than once a month) collapse into a single yearly total instead of one
// row per occurrence.
const groupByEntry = (expenses) => {
  const byId = new Map();
  for (const e of expenses) {
    const existing = byId.get(e.id);
    if (existing) {
      existing.total += e.amount;
      existing.occurrences += 1;
    } else {
      byId.set(e.id, { id: e.id, title: e.title, category: e.category, total: e.amount, occurrences: 1 });
    }
  }
  return [...byId.values()].sort((a, b) => b.total - a.total);
};

const TotalsView = ({ year, expenses, loading, onPrev, onNext }) => {
  const entries = groupByEntry(expenses);
  const grandTotal = entries.reduce((s, e) => s + e.total, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={onPrev}
          className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
          aria-label="Previous year"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h2 className="text-gray-900 font-semibold text-lg tracking-tight">{year} Totals</h2>
        <button
          onClick={onNext}
          className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
          aria-label="Next year"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">No expenses in {year}</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {entries.map(entry => (
              <div key={entry.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate">{entry.title}</span>
                  {entry.category && (
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      {entry.category}
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-900 tabular-nums">-{formatCurrency(entry.total)}</span>
                  {entry.occurrences > 1 && (
                    <span className="text-xs text-gray-400 tabular-nums">{entry.occurrences}× this year</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-card px-5 py-3 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Total</span>
        <span className="text-sm font-semibold text-gray-900 tabular-nums">-{formatCurrency(grandTotal)}</span>
      </div>
    </div>
  );
};

export default TotalsView;
